import { z } from 'zod';
import { moneySchema } from './catalog.js';
import { pageFields, pageQuery } from './common.js';
import { optionalId } from './optional-id.js';

/**
 * The operator's manual billing ledger, revenue and costs (ADR-0155, BR-259). No payment
 * gateway: payments received outside the system are recorded by billing staff. Amounts travel
 * as decimal strings (KWD, 3 decimals) and are stored as bigint fils.
 */
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v == null || v === '' ? null : v));

/** A positive KWD amount typed by the operator ("12.500"), up to 3 decimals. */
const MERCHANT_STATUSES = ['active', 'suspended', 'closed'] as const;
const EFFECTIVE_SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'cancelled',
  'expired',
  'trial_ended',
] as const;

const amountInput = z
  .string()
  .trim()
  .regex(/^\d{1,9}(\.\d{1,3})?$/);

export const BILLING_METHODS = ['bank_transfer', 'cash', 'payment_link', 'other'] as const;
export type BillingMethod = (typeof BILLING_METHODS)[number];
export const EXPENSE_CATEGORIES = [
  'hosting',
  'domain',
  'marketing',
  'devices',
  'salaries',
  'other',
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];
export const RENEWAL_BUCKETS = ['overdue', 'due_soon', 'ok', 'trial', 'none'] as const;
export type RenewalBucket = (typeof RENEWAL_BUCKETS)[number];

// ---------------------------------------------------------------- renewals
const financeMerchantSchema = z.object({
  merchantId: z.string(),
  nameEn: z.string().nullable(),
  nameAr: z.string().nullable(),
  status: z.enum(MERCHANT_STATUSES),
  ownerEmail: z.string().nullable(),
  ownerName: z.string().nullable(),
  planCode: z.string().nullable(),
  planNameEn: z.string().nullable(),
  planNameAr: z.string().nullable(),
  price: moneySchema.nullable(),
  billingPeriod: z.enum(['month', 'year']).nullable(),
  subscriptionStatus: z.enum(EFFECTIVE_SUBSCRIPTION_STATUSES).nullable(),
  endsAt: z.string().nullable(),
  /**
   * The first day not covered (the end date in the merchant's time zone, YYYY-MM-DD): an
   * extending payment starts on or after it (BR-259).
   */
  endsOn: z.string().nullable(),
  trialEndsAt: z.string().nullable(),
  /** Whole days from now to the end date (negative when overdue). */
  daysLeft: z.number().int().nullable(),
  bucket: z.enum(RENEWAL_BUCKETS),
  lastPaymentOn: z.string().nullable(),
  lastReminderAt: z.string().nullable(),
});
export type FinanceMerchant = z.infer<typeof financeMerchantSchema>;
export const renewalsSchema = z.object({
  merchants: z.array(financeMerchantSchema),
  counts: z.record(z.enum(RENEWAL_BUCKETS), z.number().int()),
});
export type Renewals = z.infer<typeof renewalsSchema>;

export const reminderResultSchema = z.object({ sentTo: z.string(), at: z.string() });

// ---------------------------------------------------------------- payments
export const recordPaymentSchema = z
  .object({
    merchantId: z.uuid(),
    amount: amountInput,
    paidOn: z.iso.date(),
    method: z.enum(BILLING_METHODS),
    reference: text(120),
    periodFrom: z.iso.date(),
    periodTo: z.iso.date(),
    note: text(500),
    /** Move the subscription end date to the end of `periodTo` (explicit choice). */
    extendSubscription: z.boolean().default(false),
  })
  .refine((p) => p.periodTo >= p.periodFrom, {
    error: 'The period must end on or after its start.',
    path: ['periodTo'],
  });
export type RecordPayment = z.input<typeof recordPaymentSchema>;

export const voidRecordSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export type VoidRecord = z.input<typeof voidRecordSchema>;

export const billingPaymentSchema = z.object({
  id: z.string(),
  merchantId: z.string(),
  merchantNameEn: z.string().nullable(),
  merchantNameAr: z.string().nullable(),
  amount: moneySchema,
  paidOn: z.string(),
  method: z.enum(BILLING_METHODS),
  reference: z.string().nullable(),
  periodFrom: z.string(),
  periodTo: z.string(),
  note: z.string().nullable(),
  /** RCPT-YYYY-NNNN, sequential per calendar year of the payment date. */
  receiptNumber: z.string(),
  extendedTo: z.string().nullable(),
  recordedByName: z.string().nullable(),
  createdAt: z.string(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
});
export type BillingPayment = z.infer<typeof billingPaymentSchema>;
export const billingPaymentListSchema = z.object({
  payments: z.array(billingPaymentSchema),
  ...pageFields,
});
export const billingPaymentQuerySchema = z.object({
  merchantId: optionalId(),
  ...pageQuery(25),
});
export type BillingPaymentQuery = z.input<typeof billingPaymentQuerySchema>;

const sellerSchema = z.object({
  nameEn: z.string(),
  nameAr: z.string(),
  detailsEn: z.string().nullable(),
  detailsAr: z.string().nullable(),
});
export const billingReceiptSchema = z.object({
  payment: billingPaymentSchema,
  seller: sellerSchema,
  buyer: z.object({ ownerName: z.string().nullable(), ownerEmail: z.string().nullable() }),
});
export type BillingReceipt = z.infer<typeof billingReceiptSchema>;

// ---------------------------------------------------------------- expenses
export const recordExpenseSchema = z.object({
  spentOn: z.iso.date(),
  category: z.enum(EXPENSE_CATEGORIES),
  amount: amountInput,
  note: text(500),
});
export type RecordExpense = z.input<typeof recordExpenseSchema>;

export const expenseSchema = z.object({
  id: z.string(),
  spentOn: z.string(),
  category: z.enum(EXPENSE_CATEGORIES),
  amount: moneySchema,
  note: z.string().nullable(),
  recordedByName: z.string().nullable(),
  createdAt: z.string(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
});
export type Expense = z.infer<typeof expenseSchema>;
const monthTotalSchema = z.object({
  month: z.string(),
  total: moneySchema,
  byCategory: z.record(z.enum(EXPENSE_CATEGORIES), moneySchema),
});
export const expenseListSchema = z.object({
  expenses: z.array(expenseSchema),
  ...pageFields,
  /** The last 12 months (newest first), voided expenses excluded. */
  monthly: z.array(monthTotalSchema),
});
export const expenseQuerySchema = z.object({ ...pageQuery(25) });

// ---------------------------------------------------------------- dashboard
export const financeDashboardSchema = z.object({
  today: z.string(),
  collectedThisMonth: moneySchema,
  collectedLastMonth: moneySchema,
  collectedThisYear: moneySchema,
  /** Active paying merchants at their plan's price, per year (informational). */
  expectedAnnual: moneySchema,
  payingMerchants: z.number().int(),
  overdueAmount: moneySchema,
  overdueMerchants: z.number().int(),
  dueSoonMerchants: z.number().int(),
  /** Paid periods that ended in the last 90 days, and how many of them were paid again. */
  renewal: z.object({ due: z.number().int(), renewed: z.number().int() }),
  expensesThisMonth: moneySchema,
  expensesThisYear: moneySchema,
});
export type FinanceDashboard = z.infer<typeof financeDashboardSchema>;

// ---------------------------------------------------------------- cost per merchant
export const profitabilityQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
});
export type ProfitabilityQuery = z.input<typeof profitabilityQuerySchema>;
const profitRowSchema = z.object({
  merchantId: z.string(),
  nameEn: z.string().nullable(),
  nameAr: z.string().nullable(),
  collected: moneySchema,
  assistantRequests: z.number().int(),
  /** Micro-USD as a digit string (bigint in the database, never a float). */
  assistantCostMicroUsd: z.string().regex(/^\d+$/),
  assistantCost: moneySchema,
  profit: moneySchema,
});
export type ProfitRow = z.infer<typeof profitRowSchema>;
export const profitabilitySchema = z.object({
  month: z.string(),
  /** KWD for one US dollar, as set by the operator. */
  usdRate: moneySchema,
  rows: z.array(profitRowSchema),
  totals: z.object({ collected: moneySchema, assistantCost: moneySchema, profit: moneySchema }),
  marketing: moneySchema,
  /** Merchants whose first (non-voided) payment falls in the month. */
  newPayingMerchants: z.number().int(),
  payingMerchants: z.number().int(),
  /** Marketing expenses ÷ new paying merchants; null when there is none. */
  cac: moneySchema.nullable(),
});
export type Profitability = z.infer<typeof profitabilitySchema>;

// ---------------------------------------------------------------- settings
export const financeSettingsSchema = z.object({
  sellerNameEn: z.string(),
  sellerNameAr: z.string(),
  sellerDetailsEn: z.string().nullable(),
  sellerDetailsAr: z.string().nullable(),
  autoReminders: z.boolean(),
  usdRate: moneySchema,
  version: z.number().int(),
});
export type FinanceSettings = z.infer<typeof financeSettingsSchema>;
export const saveFinanceSettingsSchema = z.object({
  sellerNameEn: z.string().trim().min(1).max(120),
  sellerNameAr: z.string().trim().min(1).max(120),
  sellerDetailsEn: text(500),
  sellerDetailsAr: text(500),
  autoReminders: z.boolean(),
  usdRate: amountInput,
  version: z.number().int().positive(),
});
export type SaveFinanceSettings = z.input<typeof saveFinanceSettingsSchema>;
